import { DataCenterFinancialSnapshot } from '../../financial/DataCenterFinancialSnapshot';
/**
 * CashflowStreamSection (FIN-2) — cumulative cash-position curve (editorial).
 *
 * READ-ONLY over `report.financialAnalysis.cashflow` — the engine's ACTUAL
 * annual cashflow vector, persisted on the stamp at generation time. The chart
 * plots the running cumulative position of those flows: year 0 is the CAPEX
 * outflow, each later year adds (revenue − opex), and the curve crosses zero at
 * the simple payback the headline reports.
 *
 * HISTORY (why this is worth a comment): this exhibit used to be SYNTHETIC. The
 * engine vector was not persisted, so the section drew a two-segment line
 * interpolated between the NPV and the payback year — inventing a year-0 value
 * of `−NPV/(life−payback)×payback` that was neither the CAPEX nor any engine
 * output, and mixing an UNDISCOUNTED payback with a DISCOUNTED terminal value.
 * For a negative-NPV project with a finite payback it drew a curve that rose
 * through zero and then fell again. An investment-grade report must not chart a
 * shape it did not compute.
 *
 * Chart chrome is THEME-AWARE (resolveChartGridChrome + roleColor +
 * chartPalette); no raw oklch leaks into the chart CSS (PDF-export safe). A zero
 * ReferenceLine marks breakeven.
 */
import React from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { ChartFrame } from '../../../ui/ChartFrame';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { resolveChartGridChrome } from '../../../../lib/reportChartGrid';
import { resolveReportChartPalette } from '../../../../lib/reportChartTheme';
import { useFigureNumber } from '../../report/figureCounter';
import { EditorialExhibit } from '../../report/editorial';
import { fmtUsdMillions, fmtYears } from '../../../../lib/reportRendering/financialFormat';

interface CurvePoint {
  /** Project year (0 = the stamp's `cashflow.baseYear`). */
  year: number;
  /** Calendar year label when the stamp carries a base year. */
  calendarYear: number | null;
  /** Net flow for the year (USD). */
  net: number;
  /** Running cumulative position (USD). */
  cumulative: number;
}

function isNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Build the curve from the PERSISTED engine vector. Returns [] when the stamp
 * carries no cashflow (older reports generated before the vector was persisted)
 * — the section then renders its empty state rather than inventing a shape.
 */
type StampCashflow = NonNullable<NonNullable<SiteRagReport['financialAnalysis']>['cashflow']>;

function buildCurve(cashflow: StampCashflow | undefined): CurvePoint[] {
  if (!cashflow || !Array.isArray(cashflow.flowsUsd) || cashflow.flowsUsd.length === 0) return [];
  const { flowsUsd, cumulativeUsd, baseYear } = cashflow;
  return flowsUsd.map((net, i) => ({
    year: i,
    calendarYear: isNum(baseYear) ? baseYear + i : null,
    net,
    cumulative: cumulativeUsd?.[i] ?? flowsUsd.slice(0, i + 1).reduce((a, b) => a + b, 0),
  }));
}

export const CashflowStreamSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const figNo = useFigureNumber('financial:cashflowStream');
  const h = report.financialAnalysis?.headline;
  const cashflow = report.financialAnalysis?.cashflow;
  const curve = buildCurve(cashflow);
  const hasData = curve.length > 0;

  const grid = resolveChartGridChrome(theme);
  const palette = resolveReportChartPalette(theme, 5);
  const seriesColor = palette[0]!; // brand accent series
  const axisInk = roleColor(theme, 'muted');

  const payback = h && isNum(h.paybackYears) ? h.paybackYears : null;

  if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') return <SectionShell label={label} hasData><DataCenterFinancialSnapshot stamp={report.financialAnalysis} section="cashflowStream" /></SectionShell>;

  return (
    <SectionShell label={label} hasData={hasData}>
      {hasData ? (
        <EditorialExhibit
          theme={theme}
          span="columns"
          captionNumber={figNo}
          caption={
            <>
              Cumulative undiscounted cash position from the engine&rsquo;s annual cashflow vector
              {cashflow?.basis === 'phased' ? ' (phased engine – per-phase CAPEX drawdown)' : ''}
              {isNum(cashflow?.baseYear) ? `, year 0 = ${cashflow!.baseYear} (COD)` : ''}. Year 0 carries the capital
              outflow; each later year adds revenue less operating cost. The curve crosses zero at simple payback
              {payback != null ? ` (${fmtYears(payback)})` : ''}. Discounted terminal value is the NPV reported above
              &ndash; it is not the endpoint of this undiscounted curve.
            </>
          }
        >
          {/* Carrier div holds the testid + export-capture hooks (the exhibit
              figure owns the span; EditorialExhibit has no testid prop). */}
          <div
            className="w-full"
            data-testid="financial-cashflow-chart"
            data-export-capture=""
            data-export-id="financial-cashflow-chart"
            data-export-title="Cumulative Cash Position"
          >
            <ChartFrame exportId="financial-cashflow-chart" traceName="report:financial-cashflow" minWidth={100} aspect={0.46} minHeight={240} maxHeight={360}>
              <ComposedChart data={curve} margin={{ top: 16, right: 24, bottom: 24, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid.grid} />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 11, fill: axisInk }}
                  stroke={grid.axis}
                  label={{ value: 'Year', position: 'insideBottom', offset: -8, fontSize: 11, fill: axisInk }}
                />
                <YAxis
                  tickFormatter={(v: number) => `$${Math.round(v / 1_000_000)}M`}
                  tick={{ fontSize: 11, fill: axisInk }}
                  stroke={grid.axis}
                  width={56}
                />
                <ReferenceLine y={0} stroke={grid.axis} />
                {payback != null ? (
                  <ReferenceLine
                    x={Math.round(payback)}
                    stroke={roleColor(theme, 'accent')}
                    strokeDasharray="4 4"
                    label={{ value: `Payback ${fmtYears(payback)}`, position: 'top', fontSize: 11, fill: roleColor(theme, 'accent') }}
                  />
                ) : null}
                <Tooltip
                  cursor={{ stroke: grid.axis, strokeDasharray: '3 3' }}
                  content={({ payload }) => {
                    const p = payload?.[0]?.payload as CurvePoint | undefined;
                    if (!p) return null;
                    return (
                      <div
                        className="px-2 py-1 text-xs"
                        style={{ background: roleColor(theme, 'subtleBg'), border: `1px solid ${roleColor(theme, 'hair')}` }}
                      >
                        <div className="font-bold" style={{ color: roleColor(theme, 'ink') }}>
                          {p.calendarYear != null ? p.calendarYear : `Year ${p.year}`}
                        </div>
                        <div style={{ color: roleColor(theme, 'muted') }}>Net {fmtUsdMillions(p.net)}</div>
                        <div style={{ color: roleColor(theme, 'muted') }}>Cumulative {fmtUsdMillions(p.cumulative)}</div>
                      </div>
                    );
                  }}
                />
                <Area type="monotone" dataKey="cumulative" stroke="none" fill={seriesColor} fillOpacity={0.12} isAnimationActive={false} />
                <Line type="monotone" dataKey="cumulative" stroke={seriesColor} strokeWidth={2} dot={false} isAnimationActive={false} name="Cumulative cash position" />
              </ComposedChart>
            </ChartFrame>
          </div>
        </EditorialExhibit>
      ) : null}
    </SectionShell>
  );
};
